import moment from "moment";

import * as ServerOperations from "@/Common/Operations/operations.server";

/**
 * Interface for endpoint access info
 */
interface IEndpointInfo {
    baseUri: string;
    token: string;
    ttl: number;
    tokenGeneratedDateTime: Date;
}

/**
 * An API client which fetches and caches access info for an endpoint.
 */
abstract class EndpointInfoClient {
    protected static readonly defaultTimeToLiveInMin: number = 5; // 5mn before token expired

    protected operation: ServerOperations.Server;
    protected apiVersion: string;
    private cachedAccessInfo: IEndpointInfo;

    public constructor(operation: ServerOperations.Server, apiVersion: string = "v1.0") {
        this.operation = operation;
        this.apiVersion = apiVersion;
    }

    public clearCache(): void {
        this.cachedAccessInfo = null;
    }

    /**
     * Get the access info from the cache or from the server if it's expired
     * @returns The access info
     */
    public async getAccessInfo(): Promise<IEndpointInfo> {
        if (this.cachedAccessInfo?.token && this.cachedAccessInfo.tokenGeneratedDateTime) {
            const safeTtl = this.cachedAccessInfo.ttl - 0.5; // using 30s as a safe margin
            const nowMinusTtl = moment().subtract(safeTtl, "minutes");
            if (nowMinusTtl.isBefore(moment(this.cachedAccessInfo.tokenGeneratedDateTime)))
                return this.cachedAccessInfo;
        }

        this.cachedAccessInfo = await this.getAccessInfoFromServer();
        return this.cachedAccessInfo;
    }

    /**
     * Get the access info from the server
     * @returns The access info
     */
    protected abstract getAccessInfoFromServer(): Promise<IEndpointInfo>;
}

export { EndpointInfoClient, IEndpointInfo };
